import React, {useRef, useState} from 'react';
import firebase from 'firebase/compat/app';
import 'firebase/compat/storage';
import i18next from 'i18next';
import {toast} from 'react-toastify';
import {getCampaignHue} from '../utils/campaignVisual';

const MAX_WIDTH = 1600;
const JPEG_QUALITY = 0.85;

// Réduit l'image côté navigateur : une photo de téléphone pèse vite plusieurs Mo.
const resizeImage = (file) => new Promise((resolve, reject) => {
  const img = new Image();
  const objectUrl = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, MAX_WIDTH / img.width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(objectUrl);
    canvas.toBlob(blob => (blob ? resolve(blob) : reject(new Error('resize failed'))), 'image/jpeg', JPEG_QUALITY);
  };
  img.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('invalid image'));
  };
  img.src = objectUrl;
});

/** Image de couverture d'une campagne, affichée sur sa carte et en tête de sa page. */
const CampaignCoverPicker = ({campaign, update}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const deleteStoredCover = (storagePath) => {
    if (storagePath) firebase.storage().ref(storagePath).delete().catch(() => {});
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    try {
      const blob = await resizeImage(file);
      // Un nom par envoi : l'ancienne image n'est pas resservie par le cache du navigateur
      const storagePath = `campaignCovers/${campaign.uid}/${Date.now()}.jpg`;
      const ref = firebase.storage().ref(storagePath);
      await ref.put(blob, {contentType: 'image/jpeg'});
      const url = await ref.getDownloadURL();
      deleteStoredCover(campaign.coverStoragePath);
      update({...campaign, coverPicture: url, coverStoragePath: storagePath});
    } catch (err) {
      console.log(err);
      toast.error(i18next.t('campaignSettings.coverError'));
    }
    setUploading(false);
  };

  const removeCover = () => {
    deleteStoredCover(campaign.coverStoragePath);
    update({...campaign, coverPicture: null, coverStoragePath: null});
  };

  return (
    <div className='campaignCoverPicker'>
      <span className='campaignCoverLabel'>{i18next.t('campaignSettings.cover')}</span>
      <div
        className={`campaignCoverPreview${campaign.coverPicture ? ' hasImage' : ''}`}
        style={{
          '--campaign-hue': getCampaignHue(campaign.uid),
          backgroundImage: campaign.coverPicture ? `url(${campaign.coverPicture})` : undefined,
        }}
      >
        {uploading && <span className='campaignCoverUploading'>{i18next.t('campaignSettings.coverUploading')}</span>}
      </div>
      <p className='campaignCoverHint'>{i18next.t('campaignSettings.coverHint')}</p>
      <div className='campaignCoverActions'>
        <button type='button' disabled={uploading} onClick={() => inputRef.current.click()}>
          {campaign.coverPicture ? i18next.t('campaignSettings.coverChange') : i18next.t('campaignSettings.coverAdd')}
        </button>
        {campaign.coverPicture && (
          <button type='button' className='outline' disabled={uploading} onClick={removeCover}>
            {i18next.t('campaignSettings.coverRemove')}
          </button>
        )}
      </div>
      <input ref={inputRef} type='file' accept='image/*' hidden onChange={handleFile} />
    </div>
  );
};

export default CampaignCoverPicker;
